/* ==========================================================================
   Bearly Tools core — assets/bearly-tools-core.js
   Loaded (deferred) by snippets/tool-shell.liquid, just before each tool's own
   deferred JS. window.BT exists as soon as this file runs; the automatic work
   (tool_view, legacy detection, dataLayer forwarding, bt:ready) waits until
   DOMContentLoaded + one tick, so every tool script — and every tool's own
   DOMContentLoaded handler — has already run.
   Exposes window.BearlyTools (alias window.BT):

     BT.ctx                          { prefix, tool, name, root }
     BT.track(event, params)         GA4 event, auto-prefixed: 'download_click' -> 'ps_download_click'
     BT.dropzone(el, opts)           click / keyboard / drag-and-drop file picking
     BT.runWithProgress(opts) -> Promise<result>   THE download popup for every tool:
                                     progress -> email step (only if not unlocked) -> file.
                                     ×/Escape/backdrop close it without the file. See the
                                     "Download popup" section below for opts.
     BT.requireEmail(opts) -> Promise<email>   same popup, gate only; rejects (err.dismissed)
                                     when closed
     BT.isUnlocked()                 true if this browser/customer already passed the gate
     BT.gateDismiss({ overlay, card, onDismiss, event }) -> detach()   the ×/Escape/backdrop
                                     behaviour on its own (used by the popup)
     BT.klaviyoEvent(name, props)    Klaviyo event for the known email (no-op if none, or
                                     if the visitor declined marketing cookies)
     BT.subscribe(email, opts)       add an email to the tool list; retried on a later page
                                     if Klaviyo fails. opts: { source, properties }
     BT.download(data, filename, mime)          save a file + track download_click
     BT.trueSize(svgText) -> svgText  rewrite a cut file to 72 units per inch with
                                     width/height in inches, so it imports at the same
                                     size in Design Space, Silhouette and Inkscape
     BT.toast(message)
     BT.nudge(id, { text, action, onAction, delay })   a one-time tip with a button, shown at
                                     the bottom of the screen (see "Nudges" below)
     BT.goToDownload(button)         open the Download step (desktop) or tab (phones) and
                                     bring the tool's download button into view
     BT.initRanges(scope) / BT.initSegs(scope)  (run automatically on load)
     BT.syncRange(input)             refresh a slider's fill after setting .value in code
     BT.openStep(n | element)        desktop steps accordion: open step n (1-based) or the
                                     step holding element (no-op on phones / fit off)
     BT.viewParams = {...}           extra params for <prefix>_tool_view (set any time
                                     before DOMContentLoaded finishes)
     BT.ready                        true once boot has run; listen for 'bt:ready' otherwise

   Automatic events: <prefix>_tool_view, <prefix>_related_click, <prefix>_back_click,
   and the friction signals: <prefix>_step_open, <prefix>_disabled_click,
   <prefix>_rage_click, <prefix>_tool_leave (see "Friction signals" below)
   Debug: add ?debug_mode=1 (GA4 DebugView + console). Skin preview: ?bt_skin=workshop
   ========================================================================== */
(function () {
  'use strict';
  if (window.BearlyTools) return;

  // ---------- Config ----------
  var GA_ID = 'G-G0QSCXENNN';
  var KLAVIYO_COMPANY = 'SCGZFP';
  var KLAVIYO_LIST = 'Si3Bsy';
  var KLAVIYO_REVISION = '2024-10-15';
  var CONSENT = 'We’ll send crafting tips, free SVGs and new tools. Unsubscribe anytime.';
  var SUBSCRIBE_TIMEOUT = 4000;   // fail open: never make someone wait longer than this for Klaviyo
  var DEBUG = /[?&]debug_mode=1\b/.test(location.search);

  var root = document.querySelector('[data-bt]');
  var ctx = {
    root: root,
    prefix: (root && root.dataset.btPrefix) || 'tool',
    tool: (root && root.dataset.btTool) || location.pathname.split('/').pop(),
    name: (root && root.dataset.btName) || document.title
  };

  var skinParam = new URLSearchParams(location.search).get('bt_skin');
  if (root && skinParam && /^(studio|workshop)$/.test(skinParam)) root.dataset.skin = skinParam;

  // ---------- GA4 (sends through the store's tag when present; consent-aware) ----------
  window.btDataLayer = window.btDataLayer || [];
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.btDataLayer.push(arguments); }
  var gaLoaded = false;

  function analyticsAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.analyticsProcessingAllowed === 'function') return cp.analyticsProcessingAllowed();
    } catch (e) {}
    return true;
  }

  function loadGA() {
    if (gaLoaded) return;
    gaLoaded = true;
    var s = document.createElement('script');
    s.async = true;
    s.src = 'https://www.googletagmanager.com/gtag/js?id=' + GA_ID + '&l=btDataLayer';
    document.head.appendChild(s);
    gtag('js', new Date());
    var cfg = { send_page_view: false };
    if (DEBUG) cfg.debug_mode = true;
    gtag('config', GA_ID, cfg);
  }

  // Which copy of GA4 to send through. The store's Google & YouTube app loads GA4 for
  // this same measurement ID on the main page (window.dataLayer, with its own page_view).
  // Two copies of gtag.js for one ID can't run side by side: whichever loads second is
  // ignored and every event sent to it is lost. So when the store's tag is on the page we
  // send through it, and we only load our own copy when it isn't there at all.
  var gaMode = null, gaQueue = [], gaWaitStart = 0;
  function gaOwner() {
    try { var g = window.google_tag_manager && window.google_tag_manager[GA_ID]; return (g && g.dataLayer && g.dataLayer.name) || ''; }
    catch (e) { return ''; }
  }
  function storeTagConfigured() {
    return (window.dataLayer || []).some(function (i) { return i && typeof i === 'object' && i[0] === 'config' && i[1] === GA_ID; });
  }
  function storeTagOnPage() {
    return !!document.querySelector('script[src*="googletagmanager.com/gtag/js?id=' + GA_ID + '"]:not([src*="l="])');
  }
  function storeGtag() { window.dataLayer.push(arguments); }
  function dispatch(name, q) {
    if (gaMode === 'store') storeGtag('event', name, Object.assign({ send_to: GA_ID }, q));
    else { loadGA(); gtag('event', name, q); }
  }
  function resolveGa() {
    if (gaMode) return;
    var owner = gaOwner(), waited = Date.now() - gaWaitStart;
    if (storeTagConfigured() || (owner && owner !== 'btDataLayer')) gaMode = 'store';
    else if (owner === 'btDataLayer' || waited > (storeTagOnPage() ? 10000 : 5000)) gaMode = 'own';
    if (!gaMode) { setTimeout(resolveGa, 200); return; }
    if (DEBUG) console.log('[BearlyTools] GA4 via ' + (gaMode === 'store' ? "the store's tag" : 'own tag'));
    var q = gaQueue; gaQueue = [];
    q.forEach(function (e) { dispatch(e[0], e[1]); });
  }

  // Two modes:
  //  - legacy: the tool's own script already forwards window.dataLayer events to GA4
  //    (window.<prefix>DataLayer exists). We only push to dataLayer; its bridge sends.
  //  - core: no tool bridge. We send to GA4 ourselves, and also forward any
  //    <prefix>_* events the tool pushes to window.dataLayer.
  // Checked on every call, and the forwarder is only installed at boot (after the
  // tool scripts), so a tool that creates its bridge during load is detected.
  function legacyBridge() { return !!window[ctx.prefix + 'DataLayer']; }

  function send(name, p) {
    if (!analyticsAllowed()) return;
    var q = Object.assign({ transport_type: 'beacon' }, p);
    if (DEBUG) q.debug_mode = true;
    if (gaMode) { dispatch(name, q); return; }
    gaQueue.push([name, q]);
    if (!gaWaitStart) { gaWaitStart = Date.now(); resolveGa(); }
  }

  function track(event, params) {
    var name = event.indexOf(ctx.prefix + '_') === 0 ? event : ctx.prefix + '_' + event;
    var p = Object.assign({ tool_handle: ctx.tool }, params || {});
    if (DEBUG) console.log('[BearlyTools]', name, p);
    if (legacyBridge()) {
      window.dataLayer.push(Object.assign({ event: name }, p));
      return;
    }
    window.dataLayer.push(Object.assign({ event: name, _bt: true }, p));
    send(name, p);
  }

  // Core mode only: pick up <prefix>_* events the tool pushes straight to dataLayer.
  function forwardItem(item) {
    if (!item || item._bt || typeof item.event !== 'string') return;
    if (item.event.indexOf(ctx.prefix + '_') !== 0) return;
    var p = { tool_handle: ctx.tool };
    for (var k in item) {
      if (k === 'event' || !Object.prototype.hasOwnProperty.call(item, k)) continue;
      var v = item[k];
      if (v === null || v === undefined) continue;
      p[k] = typeof v === 'object' ? JSON.stringify(v).slice(0, 100) : v;
    }
    if (DEBUG) console.log('[BearlyTools]', item.event, p);
    send(item.event, p);
  }
  function installForwarder() {
    if (legacyBridge()) return;
    var dl = window.dataLayer;
    dl.forEach(forwardItem);   // replay anything the tool pushed before boot
    var orig = dl.push;
    dl.push = function () {
      for (var i = 0; i < arguments.length; i++) forwardItem(arguments[i]);
      return orig.apply(dl, arguments);
    };
  }
  // Runs before the core fires its own view, so any <prefix>_tool_view already in
  // dataLayer came from the tool (pushed directly or via BT.track).
  function toolFiredView() {
    return window.dataLayer.some(function (i) { return i && i.event === ctx.prefix + '_tool_view'; });
  }

  // ---------- Storage (fails quietly in private modes) ----------
  function store(key, val) {
    try {
      if (val === undefined) return localStorage.getItem(key);
      localStorage.setItem(key, val);
    } catch (e) { return null; }
  }

  // ---------- One email unlocks every tool ----------
  // Each tool historically kept its own keys: PerfectScore -> ps_unlocked (+ bt_email),
  // Box It -> ba_tools_unlocked + ba_tools_email, Cyanotype -> ba_tools_email.
  // This runs before any tool script, so whatever one tool learned, all tools see.
  var EMAIL_KEYS = ['bt_email', 'ba_tools_email'];
  var FLAG_KEYS = ['ps_unlocked', 'ba_tools_unlocked'];
  function knownEmail() {
    var c = window.BT_CUSTOMER;
    if (c && c.email) return String(c.email).toLowerCase();
    for (var i = 0; i < EMAIL_KEYS.length; i++) { var v = store(EMAIL_KEYS[i]); if (v) return v; }
    return '';
  }
  function unlockEverywhere(email) {
    if (email) EMAIL_KEYS.forEach(function (k) { store(k, email); });
    FLAG_KEYS.forEach(function (k) { store(k, '1'); });
    memUnlocked = true;
  }
  // Unlocked = known email, OR an old tool's unlock flag without a stored email
  // (early PerfectScore/Box It visitors), OR passed the gate on this page while
  // storage is blocked. Those visitors must not be asked again.
  var memUnlocked = false;
  function isUnlocked() {
    if (memUnlocked || knownEmail()) return true;
    return FLAG_KEYS.some(function (k) { return store(k) === '1'; });
  }
  (function syncUnlock() {
    var email = knownEmail();
    var flagged = FLAG_KEYS.some(function (k) { return store(k) === '1'; });
    if (email || flagged) unlockEverywhere(email);
  })();

  // ---------- Klaviyo (client API) ----------
  // Tracking events follow the visitor's cookie choice (Shopify's consent banner), the
  // same way GA4 does. Signups don't: typing an email next to the consent line is the
  // visitor asking to join, so that always goes through.
  function marketingAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.marketingAllowed === 'function') return cp.marketingAllowed();
    } catch (e) {}
    return true;
  }

  function klaviyoPost(path, body) {
    return fetch('https://a.klaviyo.com/client/' + path + '/?company_id=' + KLAVIYO_COMPANY, {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error('Klaviyo ' + r.status);
      return r;
    });
  }

  function klaviyoSubscribe(email, opts) {
    opts = opts || {};
    return klaviyoPost('subscriptions', {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: opts.source || ('Bearly Tools - ' + ctx.name),
          profile: { data: { type: 'profile', attributes: { email: email, properties: opts.properties || { first_tool: ctx.name } } } }
        },
        relationships: { list: { data: { type: 'list', id: KLAVIYO_LIST } } }
      }
    });
  }

  // Downloads never wait on Klaviyo (fail open), so a signup that fails or is cut off by
  // the visitor leaving would be lost. It's saved first and cleared once Klaviyo accepts
  // it; any later tool page retries it (5 tries, 30 days). Subscribing twice is harmless.
  var PENDING_KEY = 'bt_pending_signup';
  function readPending() {
    try { var p = JSON.parse(store(PENDING_KEY) || 'null'); return p && EMAIL_RE.test(p.email || '') ? p : null; }
    catch (e) { return null; }
  }
  function clearPending(email) {
    var p = readPending();
    if (!p || p.email === email) { try { localStorage.removeItem(PENDING_KEY); } catch (e) {} }
  }
  function subscribe(email, opts) {
    opts = { source: (opts && opts.source) || ('Bearly Tools - ' + ctx.name), properties: (opts && opts.properties) || { first_tool: ctx.name } };
    var prev = readPending();
    store(PENDING_KEY, JSON.stringify({ email: email, opts: opts, tries: prev && prev.email === email ? prev.tries || 0 : 0,
      at: prev && prev.email === email ? prev.at : Date.now() }));
    return klaviyoSubscribe(email, opts).then(function (r) { clearPending(email); return r; });
  }
  function retryPending() {
    var p = readPending();
    if (!p) return;
    if ((p.tries || 0) >= 5 || Date.now() - (p.at || 0) > 30 * 864e5) { clearPending(p.email); return; }
    p.tries = (p.tries || 0) + 1;
    store(PENDING_KEY, JSON.stringify(p));
    klaviyoSubscribe(p.email, p.opts).then(function () {
      clearPending(p.email);
      if (DEBUG) console.log('[BearlyTools] saved signup sent');
    }, function (e) { if (DEBUG) console.warn('[BearlyTools] saved signup still failing', e); });
  }

  function klaviyoEvent(metric, props) {
    if (!marketingAllowed()) return Promise.resolve(false);
    var email = knownEmail();
    var properties = Object.assign({ tool: ctx.name, tool_handle: ctx.tool }, props || {});
    if (!email) {
      // No email yet: hand it to the store's Klaviyo onsite script (app embed), which
      // records it for visitors Klaviyo already knows by cookie and drops it otherwise.
      try {
        if (window.klaviyo && typeof window.klaviyo.push === 'function') {
          window.klaviyo.push(['track', metric, properties]);
          return Promise.resolve(true);
        }
      } catch (e) {}
      return Promise.resolve(false);
    }
    return klaviyoPost('events', {
      data: {
        type: 'event',
        attributes: {
          properties: properties,
          metric: { data: { type: 'metric', attributes: { name: metric } } },
          profile: { data: { type: 'profile', attributes: { email: email } } }
        }
      }
    }).then(function () { return true; }).catch(function () { return false; });
  }

  // ---------- Download popup + email gate (ONE copy for every tool) ----------
  // Standard (Sep 28 2026): "Making your file…" progress, then — only if this browser
  // isn't unlocked — the email step in the same card, then the file. The ×, Escape and a
  // backdrop click close it without the file (tool state untouched; the next download asks
  // again). Submitting always gives the file, even if Klaviyo is slow or down (fail open,
  // 4s cap). A failed export never traps the visitor: the popup closes and says so.
  //
  //   BT.runWithProgress({
  //     stages:   [{ p: 22, wait: 300, text: 'Measuring your card…' }, …]  (or plain strings)
  //     gateText: 'Your envelope is almost ready! Enter your email to get it.',
  //     work:     function (info) { build + save the file; may return a promise }
  //               info.gated: true if the email step was shown in this run
  //     doneText, errorText, button, title,
  //     (the bearly_tool metaobject's popup_* fields override these when filled)
  //     onEnd:    function (result) { re-enable your buttons }   // always called once
  //   }) -> Promise<{ status: 'done'|'dismissed'|'error'|'busy', gated, value, error }>
  //   (never rejects)
  //
  //   BT.requireEmail(opts) -> Promise<email>  same popup, gate only (kit demo / simple tools);
  //   rejects with err.dismissed = true when closed.
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  var TOKENS = ['--bt-accent', '--bt-on-accent', '--bt-surface', '--bt-surface-2', '--bt-ink',
    '--bt-muted', '--bt-line', '--bt-shadow', '--bt-focus'];

  var procCssDone = false;
  function procCss() {
    if (procCssDone) return;
    procCssDone = true;
    var s = document.createElement('style');
    s.id = 'bt-proc-css';
    s.textContent =
      '.bt-proc{position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;padding:16px;' +
        'background:rgba(15,12,14,.55);font-family:inherit;box-sizing:border-box}' +
      '.bt-proc[hidden]{display:none}' +
      '.bt-proc *,.bt-proc *::before,.bt-proc *::after{box-sizing:border-box}' +
      '.bt-proc__card{position:relative;width:100%;max-width:420px;padding:28px 26px 22px;border-radius:18px;text-align:center;' +
        'background:var(--bt-surface,#fff);border:1px solid var(--bt-line,#e6e1e3);box-shadow:var(--bt-shadow,0 20px 50px rgba(0,0,0,.25));color:var(--bt-ink,#1f1a1c)}' +
      '.bt-proc__title{margin:0 0 8px;padding:0 30px;font-size:22px;font-weight:800;line-height:1.2;letter-spacing:normal;text-transform:none;color:inherit}' +
      '.bt-proc__msg{margin:0 0 18px;min-height:21px;font-size:15px;line-height:1.45;color:var(--bt-muted,#6b6468)}' +
      '.bt-proc__bar{height:10px;border-radius:999px;overflow:hidden;background:var(--bt-surface-2,#f4f1f2)}' +
      '.bt-proc__fill{height:100%;width:0;border-radius:999px;transition:width .45s ease;' +
        'background:repeating-linear-gradient(90deg,var(--bt-accent,#E8537A) 0 14px,transparent 14px 20px)}' +
      '.bt-proc__card.is-done .bt-proc__fill{background:var(--bt-accent,#E8537A)}' +
      '.bt-proc__form{margin:4px 0 18px;text-align:left}' +
      '.bt-proc__form[hidden]{display:none}' +
      '.bt-proc__row{display:flex;gap:8px}' +
      '.bt-proc__email{flex:1;min-width:0;margin:0;padding:11px 13px;border-radius:12px;font:inherit;font-size:15px;' +
        'border:1px solid var(--bt-line,#e6e1e3);background:var(--bt-surface-2,#f4f1f2);color:var(--bt-ink,#1f1a1c)}' +
      '.bt-proc__email:focus{outline:none;border-color:var(--bt-accent,#E8537A);box-shadow:var(--bt-focus,0 0 0 3px rgba(232,83,122,.25))}' +
      '.bt-proc__go{margin:0;padding:11px 18px;border-radius:12px;cursor:pointer;white-space:nowrap;font:inherit;font-size:15px;font-weight:700;' +
        'letter-spacing:normal;text-transform:none;color:var(--bt-on-accent,#fff);background:var(--bt-accent,#E8537A);border:1px solid var(--bt-accent,#E8537A)}' +
      '.bt-proc__go:disabled{opacity:.55;cursor:not-allowed}' +
      '.bt-proc__err{margin:8px 0 0;font-size:13px;font-weight:600;color:#D93A3A}' +
      '.bt-proc__err[hidden]{display:none}' +
      '.bt-proc__fine{margin:8px 0 0;font-size:12px;line-height:1.4;color:var(--bt-muted,#6b6468)}' +
      '.bt-proc__sr{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}' +
      '.bt-gate-host{position:relative}' +
      '.bt-gate-x{position:absolute;top:8px;right:8px;z-index:2;display:grid;place-items:center;width:34px;height:34px;padding:0;margin:0;' +
        'border:0;border-radius:999px;background:transparent;color:inherit;opacity:.55;font:400 24px/1 system-ui,sans-serif;cursor:pointer}' +
      '.bt-gate-x:hover{opacity:1;background:rgba(0,0,0,.06)}' +
      '.bt-gate-x:focus-visible{opacity:1;outline:2px solid currentColor;outline-offset:2px}' +
      '@media (max-width:480px){.bt-proc__row{flex-direction:column}}' +
      '@media (prefers-reduced-motion:reduce){.bt-proc__fill{transition:none}}';
    document.head.appendChild(s);
  }

  // Shared close behaviour (also used by the popup below). opts: { overlay, card,
  // onDismiss, event } — event: GA event name (default 'email_dismiss'), or a function
  // returning one (or '' for none) at the moment of closing. Returns detach().
  function gateDismiss(opts) {
    opts = opts || {};
    var overlay = opts.overlay, card = opts.card || overlay;
    if (!overlay || !card) return function () {};
    procCss();
    var x = document.createElement('button');
    x.type = 'button';
    x.className = 'bt-gate-x';
    x.setAttribute('aria-label', 'Close without downloading');
    x.innerHTML = '<span aria-hidden="true">×</span>';
    card.classList.add('bt-gate-host');
    card.appendChild(x);
    var done = false;

    function onKey(e) {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopImmediatePropagation();   // keep full-screen/tool Escape handlers out of it
      dismiss();
    }
    function onBackdrop(e) { if (e.target === overlay) dismiss(); }
    function detach() {
      if (done) return;
      done = true;
      window.removeEventListener('keydown', onKey, true);
      overlay.removeEventListener('click', onBackdrop);
      x.remove();
      card.classList.remove('bt-gate-host');
    }
    function dismiss() {
      if (done) return;
      var ev = typeof opts.event === 'function' ? opts.event() : (opts.event === undefined ? 'email_dismiss' : opts.event);
      detach();
      if (ev) track(ev);
      if (opts.onDismiss) { try { opts.onDismiss(); } catch (e) { if (DEBUG) console.warn('[BearlyTools] onDismiss', e); } }
    }
    x.addEventListener('click', dismiss);
    window.addEventListener('keydown', onKey, true);
    overlay.addEventListener('click', onBackdrop);
    return detach;
  }

  function withTimeout(p, ms) {
    return new Promise(function (resolve, reject) {
      var t = setTimeout(function () { reject(new Error('timeout')); }, ms);
      p.then(function (v) { clearTimeout(t); resolve(v); }, function (e) { clearTimeout(t); reject(e); });
    });
  }

  var DEFAULT_STAGES = [
    { p: 22, wait: 300, text: 'Reading your design…' },
    { p: 55, wait: 700, text: 'Putting it together…' },
    { p: 84, wait: 700, text: 'Almost there…' }
  ];

  function buildProc() {
    procCss();
    var uid = 'bt-proc-' + Date.now().toString(36) + Math.floor(Math.random() * 1e4);
    var el = document.createElement('div');
    el.className = 'bt-proc';
    el.hidden = true;
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-labelledby', uid + '-t');
    el.setAttribute('aria-describedby', uid + '-m');
    el.innerHTML =
      '<div class="bt-proc__card">' +
        '<h2 class="bt-proc__title" id="' + uid + '-t">Making your file</h2>' +
        '<p class="bt-proc__msg" id="' + uid + '-m" aria-live="polite"></p>' +
        '<form class="bt-proc__form" novalidate hidden>' +
          '<div class="bt-proc__row">' +
            '<label class="bt-proc__sr" for="' + uid + '-e">Email address</label>' +
            '<input class="bt-proc__email" id="' + uid + '-e" type="email" autocomplete="email" inputmode="email" placeholder="you@example.com" aria-describedby="' + uid + '-err">' +
            '<button class="bt-proc__go" type="submit">Get my file</button>' +
          '</div>' +
          '<p class="bt-proc__err" id="' + uid + '-err" role="alert" hidden></p>' +
          '<p class="bt-proc__fine">' + CONSENT + '</p>' +
        '</form>' +
        '<div class="bt-proc__bar"><div class="bt-proc__fill"></div></div>' +
      '</div>';
    // Kit colours live on the tool root (.bt / skin). The popup sits on <body> (so no
    // transformed/sticky ancestor can clip it), so copy the resolved tokens across.
    if (root) {
      var cs = getComputedStyle(root);
      TOKENS.forEach(function (t) { var v = cs.getPropertyValue(t); if (v && v.trim()) el.style.setProperty(t, v.trim()); });
    }
    document.body.appendChild(el);
    return {
      el: el,
      card: el.querySelector('.bt-proc__card'),
      title: el.querySelector('.bt-proc__title'),
      msg: el.querySelector('.bt-proc__msg'),
      form: el.querySelector('form'),
      input: el.querySelector('input'),
      go: el.querySelector('.bt-proc__go'),
      err: el.querySelector('.bt-proc__err'),
      fill: el.querySelector('.bt-proc__fill')
    };
  }

  var running = null;   // the popup in progress (one at a time, per page)

  // Per-tool wording from the bearly_tool metaobject (popup_title, popup_steps,
  // popup_gate_text, popup_done_text, popup_button), printed by tool-shell.liquid.
  // Filled fields win over the tool's built-in wording; blank ones fall back to it.
  function popupCopy() {
    try {
      var n = root && root.querySelector('script[data-bt-popup]');
      var c = n ? JSON.parse(n.textContent) : null;
      return c && typeof c === 'object' ? c : {};
    } catch (e) { return {}; }
  }

  function runWithProgress(opts) {
    opts = opts || {};
    if (running) return Promise.resolve({ status: 'busy' });
    var copy = popupCopy();
    var steps = (copy.steps || []).filter(function (x) { return x && String(x).trim(); });
    opts = Object.assign({}, opts, {
      title: copy.title || opts.title,
      stages: steps.length ? steps : opts.stages,
      gateText: copy.gateText || opts.gateText,
      doneText: copy.doneText || opts.doneText,
      button: copy.button || opts.button
    });

    return new Promise(function (resolve) {
      var P = buildProc();
      var timers = [], detachClose = null, settled = false, gated = false, phase = 'progress';
      var lastFocus = document.activeElement;
      running = P;

      function later(fn, ms) { timers.push(setTimeout(fn, ms)); }
      function clearTimers() { timers.forEach(clearTimeout); timers = []; }
      function end(result) {
        if (settled) return;
        settled = true;
        clearTimers();
        if (detachClose) { detachClose(); detachClose = null; }
        P.el.removeEventListener('keydown', trapTab);
        P.el.remove();
        running = null;
        if (lastFocus && lastFocus.focus && document.contains(lastFocus)) { try { lastFocus.focus(); } catch (e) {} }
        result.gated = gated;
        if (opts.onEnd) { try { opts.onEnd(result); } catch (e) { if (DEBUG) console.warn('[BearlyTools] onEnd', e); } }
        resolve(result);
      }

      // Keep Tab inside the dialog.
      function trapTab(e) {
        if (e.key !== 'Tab') return;
        var f = [].slice.call(P.el.querySelectorAll('button, input')).filter(function (n) {
          return !n.disabled && !n.closest('[hidden]');
        });
        if (!f.length) { e.preventDefault(); return; }
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
      P.el.addEventListener('keydown', trapTab);

      // ×, Escape, backdrop: available through progress and the email step, so nobody is
      // ever trapped. Only a close on the email step counts as email_dismiss.
      detachClose = gateDismiss({
        overlay: P.el,
        card: P.card,
        event: function () { return phase === 'ask' ? 'email_dismiss' : 'progress_cancel'; },
        onDismiss: function () { detachClose = null; end({ status: 'dismissed' }); }
      });

      if (opts.title) P.title.textContent = opts.title;
      if (opts.button) P.go.textContent = opts.button;
      P.el.hidden = false;
      var xBtn = P.card.querySelector('.bt-gate-x');
      if (xBtn) xBtn.focus();

      function finish() {
        phase = 'finishing';
        if (detachClose) { detachClose(); detachClose = null; }   // the file is on its way
        P.form.hidden = true;
        P.fill.style.width = '100%';
        P.msg.textContent = 'Finishing up…';
        later(function () {
          var out;
          try { out = opts.work ? opts.work({ gated: gated }) : undefined; }
          catch (e) { return fail(e); }
          Promise.resolve(out).then(function (value) {
            P.card.classList.add('is-done');
            P.msg.textContent = opts.doneText || 'Your file is ready!';
            later(function () { end({ status: 'done', value: value }); }, 1100);
          }, fail);
        }, 450);
      }
      function fail(e) {
        if (DEBUG || window.console) console.error('[BearlyTools] export failed', e);
        track('export_error', { error: String((e && e.message) || e).slice(0, 100) });
        end({ status: 'error', error: e });
        toast(opts.errorText || 'Sorry, we couldn’t make your file. Please try again.');
      }

      function ask() {
        phase = 'ask';
        gated = true;
        P.msg.textContent = opts.gateText || 'Your file is almost ready! Enter your email to get it.';
        P.form.hidden = false;
        track('email_view');
        P.input.focus();
      }

      P.form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (phase !== 'ask') return;
        var email = (P.input.value || '').trim().toLowerCase();
        if (!EMAIL_RE.test(email)) {
          P.err.textContent = 'Enter a full email address, like name@example.com.';
          P.err.hidden = false;
          P.input.focus();
          return;
        }
        P.err.hidden = true;
        P.go.disabled = true;
        P.go.textContent = 'Sending…';
        phase = 'sending';
        if (detachClose) { detachClose(); detachClose = null; }
        withTimeout(subscribe(email), SUBSCRIBE_TIMEOUT)
          .catch(function (e2) { if (DEBUG) console.warn('[BearlyTools] subscribe failed', e2); })
          .then(function () {
            unlockEverywhere(email);
            track('email_submit');
            finish();
          });
      });

      // Progress stages, then gate or file.
      var stages = (opts.stages && opts.stages.length ? opts.stages : DEFAULT_STAGES).map(function (s, i, a) {
        return typeof s === 'string' ? { p: Math.round(22 + (62 * i) / Math.max(1, a.length - 1)), wait: i ? 700 : 300, text: s } : s;
      });
      var t = 0;
      P.msg.textContent = stages[0].text;
      stages.forEach(function (st) {
        later(function () { P.msg.textContent = st.text; P.fill.style.width = st.p + '%'; }, t);
        t += st.wait;
      });
      later(function () { if (isUnlocked()) finish(); else ask(); }, t);
    });
  }

  // Gate only, same popup: resolves the email (or '' for unlocked-by-flag browsers),
  // rejects with err.dismissed when closed. A double call shares one popup.
  var pendingGate = null;
  function requireEmail(opts) {
    opts = opts || {};
    if (isUnlocked()) return Promise.resolve(knownEmail());
    if (pendingGate) return pendingGate;
    pendingGate = runWithProgress({
      title: opts.title,
      gateText: opts.text,
      stages: opts.progress === false ? [{ p: 84, wait: 0, text: '' }] : opts.stages,
      doneText: opts.doneText
    }).then(function (r) {
      pendingGate = null;
      if (r.status === 'done') return knownEmail();
      var e = new Error(r.status === 'busy' ? 'busy' : 'dismissed');
      e.dismissed = r.status === 'dismissed';
      throw e;
    });
    return pendingGate;
  }

  // ---------- File download ----------
  // Every file a tool saves ends in "-BAtools" before the extension. Idempotent, and an
  // older " - BA" / "-BA" ending is replaced, so tools can pass names either way.
  function brandName(name) {
    var m = /^(.*?)(\.[a-z0-9]{1,5})?$/i.exec(String(name || 'design'));
    return m[1].replace(/\s*-\s*BA(tools)?$/i, '').replace(/\s+$/, '') + '-BAtools' + (m[2] || '');
  }
  function download(data, filename, mime) {
    var blob = data instanceof Blob ? data : new Blob([data], { type: mime || 'image/svg+xml' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = brandName(filename || ctx.tool + '.svg');
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(function () { URL.revokeObjectURL(url); }, 4000);
    track('download_click', { file_name: a.download });
  }

  // ---------- True-size SVG ----------
  // Design Space ignores physical units and reads the viewBox at 72 units/in; most
  // other apps read width/height. Writing both at 72/in sizes the file the same in
  // either. Input needs width/height with units (in, mm, cm, pt, px) and a viewBox;
  // anything else comes back unchanged.
  var UNIT_IN = { 'in': 1, mm: 1 / 25.4, cm: 1 / 2.54, pt: 1 / 72, pc: 1 / 6, px: 1 / 96 };
  function trueSize(svg) {
    var m = /<svg\b[^>]*>/i.exec(svg);
    if (!m) return svg;
    var tag = m[0];
    function attr(name) {
      var a = new RegExp('\\s' + name + '\\s*=\\s*("([^"]*)"|\'([^\']*)\')', 'i').exec(tag);
      return a ? (a[2] != null ? a[2] : a[3]) : null;
    }
    function inches(v) {
      var u = /^\s*([0-9]*\.?[0-9]+(?:e[-+]?\d+)?)\s*(in|mm|cm|pt|pc|px)\s*$/i.exec(v || '');
      return u ? parseFloat(u[1]) * UNIT_IN[u[2].toLowerCase()] : NaN;
    }
    var w = inches(attr('width')), h = inches(attr('height'));
    var vb = (attr('viewBox') || '').trim().split(/[\s,]+/).map(Number);
    if (!(w > 0 && h > 0) || vb.length !== 4 || !(vb[2] > 0 && vb[3] > 0)) return svg;
    var end = svg.lastIndexOf('</svg>');
    if (end < m.index) return svg;
    function n(x) { return String(+x.toFixed(4)); }
    var rest = tag.replace(/\s(width|height|viewBox)\s*=\s*("[^"]*"|'[^']*')/gi, '').replace(/^<svg/i, '').replace(/\/?>$/, '');
    var open = '<svg' + rest + ' width="' + n(w) + 'in" height="' + n(h) + 'in" viewBox="0 0 ' + n(w * 72) + ' ' + n(h * 72) + '">';
    var sx = w * 72 / vb[2], sy = h * 72 / vb[3];
    var xf = 'scale(' + n(sx) + (Math.abs(sx - sy) > 1e-9 ? ' ' + n(sy) : '') + ')' +
      (vb[0] || vb[1] ? ' translate(' + n(-vb[0]) + ' ' + n(-vb[1]) + ')' : '');
    return svg.slice(0, m.index) + open + '<g transform="' + xf + '">' +
      svg.slice(m.index + tag.length, end) + '</g>' + svg.slice(end);
  }

  // ---------- Toast ----------
  var toastTimer;
  function toast(message) {
    var el = document.querySelector('.bt-toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'bt-toast';
      el.setAttribute('role', 'status');
      (ctx.root || document.body).appendChild(el);
    }
    el.textContent = message;
    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { el.hidden = true; }, 3200);
  }

  // ---------- Nudges ----------
  // A short tip at the moment someone tends to stall (e.g. "Add your own SVG" after they
  // play with a sample). Shown at most once per id per browser session, never after a
  // download, and gone after 14 s, a tap on its button, or its ×.
  // Events: <prefix>_nudge_view / _nudge_click / _nudge_dismiss {nudge: id}.
  var nudgeEl = null, nudgeTimer = null;
  function nudge(id, opts) {
    opts = opts || {};
    var key = 'bt_nudge_' + ctx.prefix + '_' + id;
    try { if (sessionStorage.getItem(key)) return; } catch (e) {}
    setTimeout(function () {
      if (friction.downloaded) return;
      if (opts.when && !opts.when()) return;
      try { if (sessionStorage.getItem(key)) return; sessionStorage.setItem(key, '1'); } catch (e) {}
      if (!nudgeEl) {
        nudgeEl = document.createElement('div');
        nudgeEl.className = 'bt-nudge';
        nudgeEl.setAttribute('role', 'status');
        nudgeEl.innerHTML = '<span class="bt-nudge__text"></span>' +
          '<button type="button" class="bt-btn bt-btn--sm bt-nudge__go"></button>' +
          '<button type="button" class="bt-nudge__x" aria-label="Close tip">\u00d7</button>';
        (ctx.root || document.body).appendChild(nudgeEl);
      }
      var go = nudgeEl.querySelector('.bt-nudge__go');
      nudgeEl.querySelector('.bt-nudge__text').textContent = opts.text || '';
      go.textContent = opts.action || 'OK';
      function hide() { nudgeEl.hidden = true; clearTimeout(nudgeTimer); }
      go.onclick = function () { hide(); track('nudge_click', { nudge: id }); if (opts.onAction) opts.onAction(); };
      nudgeEl.querySelector('.bt-nudge__x').onclick = function () { hide(); track('nudge_dismiss', { nudge: id }); };
      nudgeEl.hidden = false;
      track('nudge_view', { nudge: id });
      clearTimeout(nudgeTimer);
      nudgeTimer = setTimeout(hide, 14000);
    }, opts.delay || 0);
  }
  function goToDownload(button) {
    var dock = document.querySelector('.bt-app-dock__dl');
    if (dock && dock.offsetParent !== null) { dock.click(); return; }   // phone editor
    if (button) {
      openStep(button);
      setTimeout(function () {
        try { button.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch (e) {}
        button.classList.add('bt-pulse');
        setTimeout(function () { button.classList.remove('bt-pulse'); }, 2400);
        try { button.focus({ preventScroll: true }); } catch (e) {}
      }, 60);
    }
  }

  // ---------- Dropzone ----------
  // opts: { accept: '.svg,image/svg+xml', onFile(file), input: <input type=file> (optional) }
  function dropzone(el, opts) {
    opts = opts || {};
    var input = opts.input || el.querySelector('input[type="file"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'file';
      input.hidden = true;
      el.appendChild(input);
    }
    if (opts.accept) input.accept = opts.accept;

    function accepts(file) {
      if (!opts.accept) return true;
      var name = file.name.toLowerCase();
      return opts.accept.split(',').some(function (a) {
        a = a.trim().toLowerCase();
        return a.charAt(0) === '.' ? name.slice(-a.length) === a : file.type === a;
      });
    }
    function take(file) {
      if (!file) return;
      if (!accepts(file)) {
        toast('That file type isn\'t supported. Choose a ' + (opts.accept || '').replace(/,/g, ' or ') + ' file.');
        track('file_rejected', { file_type: file.type || file.name.split('.').pop() });
        return;
      }
      track('file_upload', { file_type: file.type || file.name.split('.').pop(), file_kb: Math.round(file.size / 1024) });
      if (opts.onFile) opts.onFile(file);
    }

    input.addEventListener('change', function () { take(input.files[0]); input.value = ''; });
    el.querySelectorAll('[data-bt-pick]').forEach(function (b) {
      b.addEventListener('click', function (e) { e.preventDefault(); input.click(); });
    });
    ['dragenter', 'dragover'].forEach(function (t) {
      el.addEventListener(t, function (e) { e.preventDefault(); el.classList.add('is-over'); });
    });
    ['dragleave', 'drop'].forEach(function (t) {
      el.addEventListener(t, function (e) {
        if (t === 'dragleave' && el.contains(e.relatedTarget)) return;
        el.classList.remove('is-over');
      });
    });
    el.addEventListener('drop', function (e) {
      e.preventDefault();
      take(e.dataTransfer && e.dataTransfer.files[0]);
    });
    return { open: function () { input.click(); } };
  }

  // ---------- Ranges: fill + live value ----------
  function syncRange(r) {
    var min = parseFloat(r.min) || 0, max = parseFloat(r.max) || 100, v = parseFloat(r.value);
    r.style.setProperty('--bt-fill', ((v - min) / (max - min) * 100) + '%');
    var out = r.id && document.querySelector('[data-bt-value-for="' + r.id + '"]');
    if (out) out.textContent = r.value + (r.dataset.unit ? ' ' + r.dataset.unit : '');
  }
  // Tools sometimes set a slider's value/min/max in code (unit switches, presets),
  // which fires no input event. Re-sync every kit slider shortly after any click or change.
  var resyncTimer;
  function resyncRanges() {
    clearTimeout(resyncTimer);
    resyncTimer = setTimeout(function () {
      document.querySelectorAll('.bt-range').forEach(syncRange);
    }, 30);
  }
  function initRanges(scope) {
    (scope || document).querySelectorAll('.bt-range').forEach(function (r) {
      if (r._bt) return;
      r._bt = true;
      syncRange(r);
      r.addEventListener('input', function () { syncRange(r); });
    });
  }

  // ---------- Segmented controls: one pressed at a time, fires 'bt:change' ----------
  function initSegs(scope) {
    (scope || document).querySelectorAll('.bt-seg').forEach(function (seg) {
      if (seg._bt) return;
      seg._bt = true;
      seg.setAttribute('role', seg.getAttribute('role') || 'group');
      seg.addEventListener('click', function (e) {
        var b = e.target.closest('button');
        if (!b || !seg.contains(b)) return;
        seg.querySelectorAll('button').forEach(function (x) { x.setAttribute('aria-pressed', String(x === b)); });
        seg.dispatchEvent(new CustomEvent('bt:change', { detail: { value: b.value || b.dataset.value || b.textContent.trim() }, bubbles: true }));
      });
    });
  }

  // ---------- Steps accordion (desktop fit layout, >960px) ----------
  // The settings column used to scroll inside itself, so later steps (often Download) sat
  // below the fold. On desktop each step's header (number + title) becomes a toggle and
  // one step is open at a time; collapsed steps show just their header row, so every
  // header, Download included, stays on screen. An open step taller than the space left
  // scrolls inside its own body (bearly-tools-fit.css). Collapsed bodies keep their width
  // (height 0 + visibility hidden, not display none), so nothing a tool measured breaks.
  // Phones (app mode) keep their own one-step panel: the toggles are hidden there and the
  // column has no data-bt-acc, so none of this applies.
  // The upsell card (moved under the steps by tool-shell.liquid) goes inside the Download
  // step's body on desktop, so it shows and scrolls with that step; phones get it back.
  // Opens a step by itself when a click on the preview reveals something in a collapsed
  // step (e.g. Seamless Pattern: picking a piece shows "Edit a piece").
  // BT.openStep(n | element) opens step n (1-based) or the step holding element.
  var openStep = function () {};
  function initSteps() {
    if (!root || !root.hasAttribute('data-fit') || !window.matchMedia) return;
    var col = root.querySelector('.bt-main .bt-layout > .bt-steps');
    if (!col) return;
    var steps = Array.prototype.slice.call(col.querySelectorAll(':scope > .bt-step')).filter(function (s) {
      return s.querySelector(':scope > .bt-step__title') && s.querySelector(':scope > .bt-step__body');
    });
    if (steps.length < 2) return;
    var mq = matchMedia('(min-width: 961px)');
    var current = -1;
    var toggles = steps.map(function (s, i) {
      var title = s.querySelector(':scope > .bt-step__title');
      var body = s.querySelector(':scope > .bt-step__body');
      if (!title.id) title.id = 'bt-step-title-' + (i + 1);
      if (!body.id) body.id = 'bt-step-body-' + (i + 1);
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'bt-step__toggle';
      b.setAttribute('aria-controls', body.id);
      b.setAttribute('aria-labelledby', title.id);
      b.setAttribute('aria-expanded', 'false');
      b.addEventListener('click', function () {
        if (i !== current) trackStep(i, 'click');
        show(i === current ? -1 : i);
      });
      s.insertBefore(b, s.firstChild);
      return b;
    });
    function trackStep(i, via) {
      var t = steps[i].querySelector(':scope > .bt-step__title');
      track('step_open', { step: i + 1, step_name: t ? t.textContent.replace(/\s+/g, ' ').trim().slice(0, 40) : '', via: via });
    }
    function show(i) {
      current = i;
      steps.forEach(function (s, k) {
        s.classList.toggle('is-open', k === i);
        toggles[k].setAttribute('aria-expanded', String(k === i));
      });
    }
    openStep = function (target) {
      var i = typeof target === 'number' ? target - 1 : -1;
      if (target && target.nodeType === 1) {
        for (var k = 0; k < steps.length; k++) if (steps[k].contains(target)) { i = k; break; }
      }
      if (i >= 0 && i < steps.length && i !== current) { trackStep(i, 'auto'); show(i); }
    };
    var up = col.querySelector(':scope > .bt-up');
    var lastBody = steps[steps.length - 1].querySelector(':scope > .bt-step__body');
    function onMq() {
      if (mq.matches) col.setAttribute('data-bt-acc', ''); else col.removeAttribute('data-bt-acc');
      if (up) { if (mq.matches) lastBody.appendChild(up); else col.appendChild(up); }
    }
    if (mq.addEventListener) mq.addEventListener('change', onMq); else mq.addListener(onMq);
    onMq();
    show(0);

    // A click on the preview that un-hides something inside a collapsed step opens it.
    // Buttons, fields and the upload card in the preview area don't count (samples, uploads).
    var canvas = col.parentNode.querySelector(':scope > .bt-canvas');
    var lastPick = 0;
    if (canvas && window.MutationObserver) {
      canvas.addEventListener('pointerdown', function (e) {
        var t = e.target;
        lastPick = t.closest && t.closest('button, a, input, select, textarea, label, .bt-drop, .cy-drop') ? 0 : Date.now();
      }, true);
      new MutationObserver(function (list) {
        if (!mq.matches || Date.now() - lastPick > 400) return;
        for (var m = 0; m < list.length; m++) {
          var n = list[m].target;
          if (n.hidden) continue;
          var s = n.closest('.bt-step');
          if (s && s !== steps[current] && steps.indexOf(s) >= 0) { openStep(n); return; }
        }
      }).observe(col, { subtree: true, attributes: true, attributeFilter: ['hidden'] });
    }
  }

  // ---------- Shell events ----------
  function initShell() {
    if (!root) return;
    root.addEventListener('click', resyncRanges);
    root.addEventListener('change', resyncRanges);
    root.addEventListener('click', function (e) {
      var rel = e.target.closest('[data-bt-related]');
      if (rel) track('related_click', { to_tool: rel.dataset.btRelated });
      if (e.target.closest('[data-bt-back]')) track('back_click');
    });
  }

  // ---------- Friction signals ----------
  // Events for finding where people get stuck (they feed the tools' clarity score):
  //  <prefix>_step_open       a desktop step opened {step, step_name, via: click|auto} (above;
  //                           phones log <prefix>_app_tab from bearly-tools-app.js)
  //  <prefix>_disabled_click  a press on a button that is still disabled {target}
  //  <prefix>_rage_click      4+ presses on one spot within a second {target}
  //  <prefix>_tool_leave      the page was hidden {secs, visible_secs, last_step, started,
  //                           downloaded, seq}. Sent every time the page is hidden (a tab
  //                           switch or a phone's file picker counts too), so read the
  //                           highest seq per page view.
  // started / downloaded / last_step come from watching this tool's own events go by.
  var PASSIVE = /_(tool_view|related_click|back_click|demo_loaded|upsell_view|upsell_add|app_open|app_close|app_tab|app_download_tab|email_view|email_dismiss|email_submit|progress_cancel|fullscreen_on|fullscreen_off|xlink_click|step_open|disabled_click|rage_click|tool_leave|upload_error|export_error|file_rejected|font_load_error|missing_glyphs|mobile_ask_[a-z_]+|feedback_[a-z_]+)$/;
  var friction = { started: false, downloaded: false, lastStep: 0 };
  function noteEvent(item) {
    var name = item && item.event;
    if (typeof name !== 'string' || name.indexOf(ctx.prefix + '_') !== 0) return;
    if (item.step) friction.lastStep = item.step;
    if (/_app_download_tab$/.test(name)) friction.lastStep = 'download';
    if (/_download(_click)?$/.test(name)) friction.downloaded = true;
    if (!PASSIVE.test(name)) friction.started = true;
  }
  function watchDataLayer() {
    var dl = window.dataLayer;
    dl.forEach(noteEvent);
    var orig = dl.push;
    dl.push = function () {
      for (var i = 0; i < arguments.length; i++) noteEvent(arguments[i]);
      return orig.apply(dl, arguments);
    };
  }
  // What a press landed on, in a few words: an id, the button's label, or the element type.
  function targetName(el) {
    if (!el) return '';
    var id = el.id || '';
    var label = el.getAttribute('aria-label') || el.getAttribute('title') || (el.textContent || '').replace(/\s+/g, ' ').trim();
    return ((id ? '#' + id + ' ' : '') + (label || el.tagName.toLowerCase())).slice(0, 60);
  }
  var REPEAT_OK = 'input, select, textarea, .bt-range, [data-bt-repeat]';
  var REPEAT_LABEL = /nudge|move|undo|redo|zoom|increase|decrease|bigger|smaller|rotate|^[+\u2212-]$/i;
  function initFriction() {
    if (!root) return;
    watchDataLayer();
    var presses = [], rageUntil = 0;
    root.addEventListener('pointerdown', function (e) {
      if (e.button) return;
      var btn = e.target.closest && e.target.closest('button, [role="button"]');
      if (btn && (btn.disabled || btn.getAttribute('aria-disabled') === 'true')) {
        track('disabled_click', { target: targetName(btn) });
      }
      var spot = btn || e.target;
      if (spot.closest && spot.closest(REPEAT_OK)) return;
      if (btn && REPEAT_LABEL.test(targetName(btn).replace(/^#\S+ /, ''))) return;
      var now = Date.now();
      presses = presses.filter(function (q) { return now - q.t < 1000; });
      presses.push({ t: now, x: e.clientX, y: e.clientY, el: spot });
      var same = presses.filter(function (q) { return q.el === spot && Math.abs(q.x - e.clientX) < 30 && Math.abs(q.y - e.clientY) < 30; });
      if (same.length >= 4 && now > rageUntil) {
        rageUntil = now + 3000;
        presses = [];
        track('rage_click', { target: targetName(spot) });
      }
    }, true);

    var seq = 0, visibleMs = 0, shownAt = document.visibilityState === 'hidden' ? 0 : Date.now();
    function leave() {
      if (!shownAt) return;                       // already hidden
      visibleMs += Date.now() - shownAt;
      shownAt = 0;
      seq++;
      track('tool_leave', {
        secs: Math.round((window.performance && performance.now ? performance.now() : visibleMs) / 1000),
        visible_secs: Math.round(visibleMs / 1000),
        last_step: String(friction.lastStep || 0),
        started: friction.started ? 'yes' : 'no',
        downloaded: friction.downloaded ? 'yes' : 'no',
        seq: seq
      });
    }
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') leave(); else if (!shownAt) shownAt = Date.now();
    });
    window.addEventListener('pagehide', leave);
  }

  /* ---- Shared SVG safety: every tool that reads an uploaded SVG goes through these ----
     BT.cleanSvgText(text): drop the DOCTYPE and any entities it declared (no entity bombs,
     no external entity fetches). Call on the file text BEFORE DOMParser.
     BT.sanitizeSvg(root, { keepDataImages }): strip anything in a parsed SVG that could run
     code or reach the network: script-capable elements, animations (they can rewrite a link
     into javascript:), on* handlers, links (unwrapped to plain <g>), outside href/src, and
     outside url()/@import in styles. Internal refs (#id, url(#id)) are kept, so gradients,
     clip paths and <use> still work. keepDataImages keeps <image> whose href is an embedded
     data:image/ picture (for tools that show artwork); otherwise <image> is dropped.
     Returns root. */
  var SVG_DROP = {
    script: 1, foreignobject: 1, iframe: 1, embed: 1, object: 1, video: 1, audio: 1, canvas: 1,
    animate: 1, animatemotion: 1, animatetransform: 1, animatecolor: 1, set: 1, discard: 1,
    handler: 1, listener: 1, feimage: 1
  };
  var SVG_NS = "http://www.w3.org/2000/svg", XLINK_NS = "http://www.w3.org/1999/xlink";

  function cleanSvgText(t) {
    return String(t == null ? "" : t)
      .replace(/<!DOCTYPE[^[>]*(\[[\s\S]*?\])?\s*>/i, "")
      .replace(/&(?!(?:amp|lt|gt|quot|apos|#\d+|#x[0-9a-f]+);)[\w.:-]+;/gi, "");
  }
  function scrubCss(css) {
    return String(css)
      .replace(/@import[^;]*;?/gi, "")
      .replace(/url\(\s*(?!["']?#)[^)]*\)/gi, "none")
      .replace(/expression\s*\(/gi, "x(")
      .replace(/javascript\s*:/gi, "");
  }
  function badUrl(v) {
    var x = String(v).replace(/[\u0000- \u007f-\u009f]+/g, "").toLowerCase();
    return /^(javascript|vbscript|livescript):/.test(x) ||
           /^data:(?!image\/(png|jpe?g|gif|webp|avif|bmp);)/.test(x);
  }
  function sanitizeSvg(root, opts) {
    if (!root || !root.querySelectorAll) return root;
    opts = opts || {};
    var all = [root].concat([].slice.call(root.querySelectorAll("*")));
    all.forEach(function (n) {
      if (!n.parentNode && n !== root) return;
      var ln = (n.localName || "").toLowerCase();
      if (SVG_DROP[ln] === 1) { n.remove(); return; }
      if (ln === "image") {
        var h = n.getAttribute("href") || n.getAttributeNS(XLINK_NS, "href") || "";
        if (!(opts.keepDataImages && /^\s*data:image\/(png|jpe?g|gif|webp|avif|bmp);/i.test(h))) { n.remove(); return; }
      }
      if (ln === "a" && n !== root && n.parentNode) {           // links become plain groups
        var g = n.ownerDocument.createElementNS(SVG_NS, "g");
        [].slice.call(n.attributes).forEach(function (a) {
          if (a.localName !== "href" && a.name !== "target") { try { g.setAttribute(a.name, a.value); } catch (e) {} }
        });
        while (n.firstChild) g.appendChild(n.firstChild);
        n.parentNode.replaceChild(g, n);
        n = g; ln = "g";
      }
      if (ln === "style") n.textContent = scrubCss(n.textContent);
      [].slice.call(n.attributes).forEach(function (a) {
        var name = a.name.toLowerCase(), local = (a.localName || "").toLowerCase(), v = a.value;
        if (name.indexOf("on") === 0) { n.removeAttributeNode(a); return; }
        if (local === "href" || local === "src" || local === "action" || local === "formaction") {
          var t = String(v).trim();
          if (t.charAt(0) !== "#" && !(ln === "image" && opts.keepDataImages && !badUrl(t))) n.removeAttributeNode(a);
          return;
        }
        if (badUrl(v)) { n.removeAttributeNode(a); return; }
        if (name === "style" || /url\(|javascript\s*:|expression\s*\(/i.test(v)) {
          var c = scrubCss(v);
          if (c !== v) n.setAttribute(a.name, c);
        }
      });
    });
    return root;
  }

  var BT = {
    ctx: ctx,
    track: track,
    cleanSvgText: cleanSvgText,
    sanitizeSvg: sanitizeSvg,
    requireEmail: requireEmail,
    isUnlocked: isUnlocked,
    runWithProgress: runWithProgress,
    gateDismiss: gateDismiss,
    klaviyoEvent: klaviyoEvent,
    subscribe: subscribe,
    email: knownEmail,
    download: download,
    trueSize: trueSize,
    brandName: brandName,
    toast: toast,
    nudge: nudge,
    goToDownload: goToDownload,
    dropzone: dropzone,
    initRanges: initRanges,
    initSegs: initSegs,
    syncRange: syncRange,
    openStep: function (target) { openStep(target); },
    viewParams: null,
    ready: false
  };
  window.BearlyTools = window.BT = BT;

  // ---------- Boot ----------
  // This file is deferred and loads BEFORE the tool's own deferred script, so boot
  // must wait: DOMContentLoaded fires only after every deferred script has run, and
  // the extra tick lets tools' own DOMContentLoaded handlers finish first. By then:
  //  - a tool that fired its own <prefix>_tool_view is seen, so we don't fire a second
  //  - a tool's legacy bridge (window.<prefix>DataLayer) exists, so no forwarder
  //  - BT.viewParams set by the tool is included
  //  - tools listening for 'bt:ready' have attached their listener
  var booted = false;
  function boot() {
    if (booted) return;
    booted = true;
    initRanges();
    initSegs();
    initShell();
    try { initFriction(); } catch (e) { if (DEBUG) console.warn('[BT] friction', e); }
    try { initSteps(); } catch (e) { if (DEBUG) console.warn('[BT] steps', e); }
    try { retryPending(); } catch (e) {}
    var toolViewDone = toolFiredView();
    installForwarder();
    if (!toolViewDone) track('tool_view', Object.assign({ page_location: location.href }, BT.viewParams || {}));
    BT.ready = true;
    document.dispatchEvent(new CustomEvent('bt:ready', { detail: BT }));
  }
  function bootSoon() { setTimeout(boot, 0); }
  if (document.readyState === 'complete') {
    bootSoon();
  } else {
    document.addEventListener('DOMContentLoaded', bootSoon);
    window.addEventListener('load', bootSoon);   // backstop; boot() only runs once
  }
})();